import { Box } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import GetHiredHero from '../components/gethired/GetHiredHero';
import GetHiredProblem from '../components/gethired/GetHiredProblem';
import GetHiredTestimonials from '../components/gethired/GetHiredTestimonials';
import GetHiredPlatforms from '../components/gethired/GetHiredPlatforms';
import GetHiredObjections from '../components/gethired/GetHiredObjections';
import GetHiredOutcomes from '../components/gethired/GetHiredOutcomes';
import GetHiredCTABand from '../components/gethired/GetHiredCTABand';
import { CodeverseMentorSection } from '../components/CodeverseMentorSection';
import WebinarRegistration from '../components/webinar/WebinarRegistration';
import { useAppData } from '../store/dataStore';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAffiliateCode } from '../hooks/useAffiliateCode';

const GetHiredLanding = () => {
  const appData = useAppData();
  const { trackEvent } = useAnalytics();
  useAffiliateCode('get_hired_visit');

  const course = appData.course1;

  // Format start date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return 'soon';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Hero section data
  const heroData = {
    headline: "Get the 'AI Engineer' Job Offer On Your Email 💼",
    subheading:
      "Build actual AI apps that impress recruiters. Win interviews with a portfolio that speaks for itself.",
    heroImage:
      "https://ojasa.b-cdn.net/ojasa/marketing/student_success.png",
    coursePrice: course?.price || "59,994",
    originalPrice: course?.mrp || "89,991",
    placementRate: appData.placementRate || 85,
    studentCount: appData.studentsCount || 150,
    startDate: formatDate(course?.startDate),
  };

  // Problem section data
  const problemData = {
    title: '🚨 Why You\'re Not Getting Interviews (Yet)',
    subtitle: "Here's what's actually holding you back:",
    problems: [
      {
        title: '📚 Your Skills Look Old',
        description:
          "You learned Python basics, but recruiters want AI builders. While you're studying tutorials, others are shipping GenAI apps. That's the gap.",
      },
      {
        title: '💼 Your Portfolio Says Nothing',
        description:
          "Tutorial projects? Recruiters see those 100x per day. They want to see YOU shipped something real. A RAG chatbot. An API. Something with your name on it that actually works.",
      },
      {
        title: "⚡ You Don't Know the Secret Weapon",
        description:
          "Every pro dev at Google/Microsoft uses Claude, Copilot, and AI tools to move fast. You're coding manually in 2025. You're literally competing with one hand tied behind your back.",
      },
    ],
    cta: '💡 Here\'s what changes everything:',
  };

  // Platforms section data
  const platformsData = {
    title: "🛠️ The 4 Tools That Unlock Interview Wins",
    subtitle:
      "We built these specifically to turn you into a hire-able developer. You get all 4 during the course.",
  };

  // Outcomes section data
  const outcomesData = {
    title: "After 24 Weeks, You'll Be Interview-Ready 🎯",
    stats: [
      {
        number: "85%",
        label: "Get Offers",
      },
      {
        number: "₹12L+",
        label: "Avg First Salary",
      },
      {
        number: "12+",
        label: "Shipped Projects",
      },
    ],
    outcomes: [
      {
        title: "🚀 Ship Real Apps",
        description: "5-6 production-ready apps on your GitHub. Not tutorials. Real stuff with users.",
      },
      {
        title: "💬 Win Coding Interviews",
        description: "Live code with confidence. You've done this 100 times in training. Interviewers notice.",
      },
      {
        title: "📞 Get Called for Interviews",
        description: "Recruiters will find you. Your portfolio + profile + resume = hire-able.",
      },
      {
        title: "💼 Negotiate Better Offers",
        description: "With proof of what you can build, salary negotiations get easier. You have leverage.",
      },
    ],
  };

  // Codeverse mentor section data
  const codeverseMentorData = {
    badge: "Exclusive Learning Tool",
    title: "Code Without Leaving Your Editor",
    subtitle:
      "CodeverseMentor guides your projects step-by-step, right inside VS Code. Learn faster, build better habits.",
    comparisonTable: {
      codeverseColumn: "CodeverseMentor",
      otherToolsColumn: "Regular Tutorials",
      rows: [
        { feature: "Step-by-step guidance without leaving VS Code" },
        { feature: "Real-time code validation" },
        { feature: "Context-aware assistance" },
        { feature: "Progress tracking with milestones" },
        { feature: "Habit formation through micro-steps" },
      ],
    },
    featuresTitle: "How CodeverseMentor Accelerates Your Learning",
    features: [
      {
        title: "Guided Project Execution",
        description:
          "Curated projects broken into achievable steps with clear objectives. Learn without context-switching.",
      },
      {
        title: "Smart Hints System",
        description:
          "Get unstuck without spoilers. Context-aware guidance helps you think like a developer.",
      },
      {
        title: "Real-Time Validation",
        description:
          "Instant feedback on your code. Know immediately if you're on the right track.",
      },
      {
        title: "Habit Formation",
        description:
          "Repeated, guided practice builds muscle memory and confident developers.",
      },
      {
        title: "Zero Context-Switching",
        description:
          "Everything happens in VS Code. Stay in your flow state while learning.",
      },
      {
        title: "Progress Milestones",
        description:
          "Track your journey with visual progress markers and achievement badges.",
      },
    ],
    freeAccess: {
      badge: "Included Free",
      title: "Free Access During the Course",
      description:
        "Use CodeverseMentor for free throughout your entire learning journey. Available exclusively to all enrolled students.",
      buttonText: "Get CodeverseMentor",
      link: "https://marketplace.visualstudio.com",
    },
  };

  // Webinar registration data (will be passed to WebinarRegistration component)
  const webinarData = {
    ctaLabel: "Register Now",
    formFields: [
      { name: "fullName", label: "Full Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: false },
    ],
  };

  const handleEnrollClick = (source) => {
    trackEvent("enroll_click", { source, courseId: "1" });
  };

  const handleWebinarClick = () => {
    trackEvent("webinar_cta_click", { page: "get_hired" });
  };

  return (
    <>
      <Helmet>
        <title>Land Your Dream Job with AI Skills | Ojasa Mirai</title>
        <meta
          name="description"
          content="Master AI skills and land your dream job in 24 weeks. Build real projects, get job-ready, and transform your career with Ojasa Mirai."
        />
        <meta
          name="keywords"
          content="AI jobs, GenAI training, career transition, Python, machine learning, placement assistance"
        />
        <meta property="og:url" content="https://ojasamirai.com/ai-career" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Land Your Dream Job with AI Skills | Ojasa Mirai" />
        <meta
          property="og:description"
          content="Master AI skills and land your dream job in 24 weeks. Build real projects, get job-ready."
        />
        <meta property="og:image" content="https://images.ojasamirai.com/ojasa/marketing/win.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Land Your Dream Job with AI Skills | Ojasa Mirai" />
        <meta name="twitter:description" content="Master AI skills and land your dream job in 24 weeks. Build real projects, get job-ready." />
        <meta name="twitter:image" content="https://images.ojasamirai.com/ojasa/marketing/win.jpg" />
      </Helmet>

      <Box bg="white">

        <GetHiredHero data={heroData} onEnrollClick={handleEnrollClick} />

        <GetHiredProblem data={problemData} />

        <GetHiredTestimonials />

        <GetHiredPlatforms data={platformsData} />

        <GetHiredCTABand
          text="Still on the fence? Come to a free live demo. See what an actual GenAI project looks like."
          buttonText="Join Free Demo →"
          onButtonClick={handleWebinarClick}
        />

        <GetHiredOutcomes data={outcomesData} />

        <CodeverseMentorSection data={codeverseMentorData} />

        <GetHiredObjections />

        <GetHiredCTABand
          text="Ready to actually win interviews? Register for a free live session. Ask us anything."
          buttonText="Register (Free) →"
          onButtonClick={handleWebinarClick}
        />

        {/* Webinar Registration Section - Inline */}
        <Box id="webinar-register">
          <WebinarRegistration data={webinarData} />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default GetHiredLanding;
